/**
 * Mi trabajo — flujo de la jornada (BHM-CLD-REQ-045): ANTERIOR · EN EJECUCIÓN · SIGUIENTE bajo Nexus.
 * Estado = API (GET /activity/flow). Nada se inventa: lo que BarmetHub aún no tiene se declara como «no disponible aún».
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { activityApi, errText, fmtHms, type FlowCurrent, type FlowNext, type FlowPrevious, type WorkdayFlow } from '../../stores/activityApi';
import { TaskCode } from './TaskCode';
import { workApi } from '../../stores/workApi';
import { Icon, Pill, ErrorBanner, btnGhost, btnPrimary } from './WorkUi';
import { useNexusAccess, nexusTaskUrl } from './useNexusAccess';
import { ACTIVITY_CHANGED, notifyActivityChanged } from './WorkdayCounter';
import {
  FLOW_TABS, ORIGIN_META, defaultFlowTab, fmtClockTime, nexusPill, nexusStripNote, previousAttributed, previousResult, previousWhen, tickedElapsed, unavailableLine, type FlowTab,
} from './flowLogic';

const FLOW_POLL_MS = 30_000;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[10rem_1fr]">
      <dt className="text-[11px] uppercase tracking-wider text-on-surface-variant">{label}</dt>
      <dd className="text-sm text-on-surface">{children}</dd>
    </div>
  );
}

const projectLabel = (p: { name: string; clientName: string | null }) => `${p.name}${p.clientName ? ` · ${p.clientName}` : ''}`;

function PreviousPanel({ p }: { p: FlowPrevious }) {
  const when = previousWhen(p);
  const origin = ORIGIN_META[p.origin];
  return (
    <dl className="space-y-2">
      <Row label="Tarea">{p.task?.title ?? 'Sin tarea concreta'}</Row>
      <Row label="Proyecto">{projectLabel(p.project)}</Row>
      <Row label="Origen">{origin ? <Pill className={origin.cls}>{origin.label}</Pill> : p.origin}</Row>
      <Row label="Resultado">{previousResult(p.result)}</Row>
      <Row label={when.label}>{when.value}</Row>
      <Row label="Tiempo atribuido">{previousAttributed(p.attributedSec)}</Row>
    </dl>
  );
}

function CurrentPanel({ c, fetchedAt, now, nexusOk }: { c: FlowCurrent; fetchedAt: number; now: number; nexusOk: boolean }) {
  const navigate = useNavigate();
  const nx = nexusPill(c.nexus);
  return (
    <>
      <dl className="space-y-2">
        <Row label="Tarea">{c.task?.title ?? 'Trabajando en el proyecto (sin tarea concreta)'}</Row>
        <Row label="Proyecto"><Link className="hover:text-[#FF5E1E]" to={`/proyectos/${c.project.id}`}>{c.project.name}</Link>{c.project.clientName ? ` · ${c.project.clientName}` : ''}</Row>
        {c.description && <Row label="Descripción"><span className="whitespace-pre-line">{c.description}</span></Row>}
        <Row label="Responsable">{c.responsible?.name ?? 'Sin responsable asignado'}</Row>
        <Row label="Estado">{c.status ?? '—'}</Row>
        <Row label="Desde">{fmtClockTime(c.startedAt)} · <span className="tabular-nums">{fmtHms(tickedElapsed(c.elapsedSec, fetchedAt, now))}</span></Row>
        <Row label="Nexus">{nx ? <Pill className={nx.cls}>{nx.text}</Pill> : 'Sin ejecución de Nexus abierta'}</Row>
        {nexusOk && c.task && (
          <div className="pt-1">
            <button className={btnPrimary} data-testid="current-resolve-nexus" onClick={() => navigate(nexusTaskUrl(c.project.id, c.task!.id))}>
              <Icon name="bolt" className="text-base" />Resolver con Nexus
            </button>
          </div>
        )}
      </dl>
    </>
  );
}

function NextPanel({ n, blocked, nexusOk, busy, onStart }: { n: FlowNext | null; blocked: FlowNext[]; nexusOk: boolean; busy: boolean; onStart: (n: FlowNext) => void }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-3">
      {n ? (
        <dl className="space-y-2" data-testid="flow-next">
          <Row label="Tarea"><TaskCode code={n.task.taskCode} />{n.task.title}</Row>
          <Row label="Proyecto">{projectLabel(n.project)}</Row>
          <Row label="Preparación"><Pill className={n.readiness === 'READY' ? 'border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]' : 'border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]'}>{n.readiness}</Pill></Row>
          <Row label="Vence">{n.task.dueDate ?? 'Sin fecha'}</Row>
          <div className="flex flex-wrap gap-2 pt-1">
            <button className={btnPrimary} data-testid="next-start" disabled={busy} onClick={() => onStart(n)}><Icon name="play_arrow" className="text-base" />Iniciar tiempo</button>
            {nexusOk && <button className={btnGhost} onClick={() => navigate(nexusTaskUrl(n.project.id, n.task.id))}><Icon name="bolt" className="text-base" />Resolver con Nexus</button>}
          </div>
        </dl>
      ) : (
        <p className="text-sm text-on-surface-variant" data-testid="flow-empty-next">No hay ninguna tarea lista para empezar.</p>
      )}
      {blocked.length > 0 && (
        <div className="space-y-2" data-testid="flow-blocked">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#F43F5E]">Bloqueadas — no se ofrecen como siguiente</p>
          <ul className="space-y-1">
            {blocked.map((b) => (
              <li key={b.task.id} className="rounded-lg border border-[#F43F5E]/30 px-3 py-2 text-sm">
                <Pill className="border-[#F43F5E]/40 bg-[#F43F5E]/10 text-[#F43F5E]">BLOCKED</Pill>{' '}
                <span className="text-on-surface">{b.task.title}</span>{' '}
                <span className="text-on-surface-variant">· {b.project.name}</span>
                {b.blockedReason && <span className="block text-xs text-on-surface-variant">{b.blockedReason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * `refreshKey`: súbelo para volver a pedir el flujo. También se refresca con `notifyActivityChanged()`.
 * `onStarted`: se llama tras iniciar el temporizador desde «Siguiente» (la página recarga tareas y barra de tiempo).
 */
export default function MyWorkFlow({ refreshKey = 0, onStarted }: { refreshKey?: number; onStarted?: () => void | Promise<void> }) {
  const nexusOk = useNexusAccess();
  const [flow, setFlow] = useState<WorkdayFlow | null>(null);
  const [fetchedAt, setFetchedAt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [explicit, setExplicit] = useState<FlowTab | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const f = await activityApi.flow();
      setFlow(f); setFetchedAt(Date.now()); setError(null);
    } catch (e) { setError(errText(e, 'No se pudo cargar tu flujo de trabajo')); }
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load, refreshKey]);
  useEffect(() => {
    const h = () => { void load(); };
    window.addEventListener(ACTIVITY_CHANGED, h);
    const poll = setInterval(h, FLOW_POLL_MS); // como el contador de jornada: el servidor manda
    return () => { window.removeEventListener(ACTIVITY_CHANGED, h); clearInterval(poll); };
  }, [load]);
  useEffect(() => {
    if (!flow?.current) return;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [flow?.current]);

  const tab = defaultFlowTab(flow, explicit);
  const start = async (n: FlowNext) => {
    setBusy(true);
    try {
      await workApi.startTimer(n.project.id, n.task.id);
      notifyActivityChanged();
      await onStarted?.();
    } catch (e) { setError(errText(e)); }
    setBusy(false);
  };

  const unavailable = flow ? unavailableLine(flow.unavailable) : null;
  const state = flow?.workday;

  return (
    <section className="space-y-3" aria-label="Flujo de trabajo" data-testid="my-work-flow">
      {nexusOk && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#FF5E1E]/30 bg-[#FF5E1E]/5 px-4 py-3" data-testid="nexus-strip">
          <p className="flex items-center gap-2 text-sm font-semibold text-on-surface">
            <Icon name="bolt" className="text-[#FF5E1E]" />NEXUS<span className="font-normal text-on-surface-variant">{` · ${nexusStripNote(flow?.current?.nexus)}`}</span>
          </p>
          <Link to="/nexus" className={btnGhost}>Abrir Nexus</Link>
        </div>
      )}

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Anterior, en ejecución y siguiente">
        {FLOW_TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} data-testid={`flow-tab-${t.id}`} onClick={() => setExplicit(t.id)}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-semibold tracking-wide ${tab === t.id ? 'border-[#FF5E1E] bg-[#FF5E1E]/15 text-[#FF5E1E]' : 'border-outline-variant text-on-surface-variant hover:bg-surface-container-highest'}`}>
            <Icon name={t.icon} filled={t.id === 'current'} className={t.id === 'current' && flow?.current ? 'text-base text-[#10B981]' : 'text-base '} />{t.label}
          </button>
        ))}
        {state?.state === 'FINISHED' && <Pill className="ml-auto border-outline text-on-surface-variant">JORNADA FINALIZADA{state.clockOut ? ` · ${fmtClockTime(state.clockOut)}` : ''}</Pill>}
        {state?.staleOpen && <Pill className="ml-auto border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">Jornada abierta desde un día anterior</Pill>}
      </div>

      {loading && <p className="text-sm text-on-surface-variant">Cargando…</p>}
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {flow && (
        <div className="space-y-3 rounded-xl border border-outline-variant bg-surface-container-low p-4" role="tabpanel" data-testid={`flow-panel-${tab}`}>
          {tab === 'previous' && (flow.previous
            ? <PreviousPanel p={flow.previous} />
            : <p className="text-sm text-on-surface-variant" data-testid="flow-empty-previous">Todavía no hay trabajo anterior en los últimos 30 días.</p>)}
          {tab === 'current' && (flow.current
            ? <CurrentPanel c={flow.current} fetchedAt={fetchedAt} now={now} nexusOk={nexusOk} />
            : <p className="text-sm text-on-surface-variant" data-testid="flow-empty-current">No hay nada en ejecución ahora mismo. Empieza por la pestaña SIGUIENTE.</p>)}
          {tab === 'next' && <NextPanel n={flow.next} blocked={flow.blocked} nexusOk={nexusOk} busy={busy} onStart={start} />}
          {unavailable && <p className="text-[11px] text-outline" data-testid="flow-unavailable">{unavailable}</p>}
        </div>
      )}
    </section>
  );
}
